import type { ExpressionPhrasing, ExpressionShape } from "../en/expressionPhrases.ts";

/**
 * Spanish phrase bank for Writing Expressions (ADR 0123 §3). Same shapes and
 * placeholders as the English file.
 *
 * There is no "{b} menos que ...": read literally, "5 menos que un número" is
 * often taken as `5 - n`, the reverse of the English "less than". Subtraction
 * is "disminuido en" throughout. These phrasings follow textbook usage and have
 * not been read by a native-speaking teacher.
 */
export const expressionPhrases: Record<ExpressionShape, ExpressionPhrasing[]> = {
    S1: [
        { text: "la suma de un número\u00A0{v} y\u00A0{b}" },
        { text: "{b} más que un número\u00A0{v}" },
        { text: "un número\u00A0{v} aumentado en\u00A0{b}" },
    ],
    S2: [
        { text: "un número\u00A0{v} disminuido en\u00A0{b}" },
        { text: "la diferencia entre un número\u00A0{v} y\u00A0{b}" },
    ],
    S3: [
        { text: "{b} disminuido en un número\u00A0{v}" },
        { text: "un número\u00A0{v} restado de\u00A0{b}" },
        { text: "la diferencia entre\u00A0{b} y un número\u00A0{v}" },
    ],
    S4: [
        { text: "el producto de\u00A0{a} y un número\u00A0{v}" },
        { text: "{a} veces un número\u00A0{v}" },
        { text: "el doble de un número\u00A0{v}", twice: true },
    ],
    S5: [
        { text: "el cociente entre un número\u00A0{v} y\u00A0{a}" },
        { text: "un número\u00A0{v} dividido entre\u00A0{a}" },
    ],
    S6: [
        { text: "el cociente entre\u00A0{b} y un número\u00A0{v}" },
        { text: "{b} dividido entre un número\u00A0{v}" },
    ],
    T1: [
        { text: "{b} más que el producto de\u00A0{a} y un número\u00A0{v}" },
        { text: "el producto de\u00A0{a} y un número\u00A0{v}, aumentado en\u00A0{b}" },
        { text: "{b} más que el doble de un número\u00A0{v}", twice: true },
    ],
    T2: [
        { text: "el producto de\u00A0{a} y un número\u00A0{v}, disminuido en\u00A0{b}" },
        { text: "el doble de un número\u00A0{v}, disminuido en\u00A0{b}", twice: true },
    ],
    T3: [
        { text: "{b} disminuido en el producto de\u00A0{a} y un número\u00A0{v}" },
        { text: "la diferencia entre\u00A0{b} y el producto de\u00A0{a} y un número\u00A0{v}" },
        { text: "{b} disminuido en el doble de un número\u00A0{v}", twice: true },
    ],
    T4: [
        { text: "{a} veces la suma de un número\u00A0{v} y\u00A0{b}" },
        { text: "el producto de\u00A0{a} y la suma de un número\u00A0{v} y\u00A0{b}" },
        { text: "el doble de la suma de un número\u00A0{v} y\u00A0{b}", twice: true },
    ],
    T5: [
        { text: "{a} veces la diferencia entre un número\u00A0{v} y\u00A0{b}" },
        { text: "el producto de\u00A0{a} y la diferencia entre un número\u00A0{v} y\u00A0{b}" },
        { text: "el doble de la diferencia entre un número\u00A0{v} y\u00A0{b}", twice: true },
    ],
    T6: [
        { text: "{b} más que el cociente entre un número\u00A0{v} y\u00A0{a}" },
        { text: "el cociente entre un número\u00A0{v} y\u00A0{a}, aumentado en\u00A0{b}" },
    ],
};
